import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Filter, X } from "lucide-react";
import { useGetCategories } from "@/hooks/useCategories";
import type { TriageQueueFilters } from "@/dtos/TriageQueueDto";
import { useState } from "react";

interface TriageFiltersProps {
  filters: TriageQueueFilters;
  onFiltersChange: (filters: TriageQueueFilters) => void;
}

export const TriageFilters = ({ filters, onFiltersChange }: TriageFiltersProps) => {
  const { data: categoriesResponse } = useGetCategories();
  const categories = Array.isArray(categoriesResponse) ? categoriesResponse : categoriesResponse?.data;
  const [showFilters, setShowFilters] = useState(false);
  
  const handleClearFilters = () => {
    onFiltersChange({
      page: 1,
      limit: filters.limit || 20,
    });
  };

  const hasActiveFilters = 
    filters.category ||
    filters.subcategory ||
    filters.neighborhood ||
    filters.startDate ||
    filters.endDate ||
    filters.hasAttachments !== undefined;

  return (
    <Card className="p-4">
      <div className="space-y-4">
        {/* Header com botão de toggle */}
        <div className="flex items-center justify-between">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowFilters(!showFilters)}
            className="cursor-pointer"
          >
            <Filter className="w-4 h-4 mr-2" />
            Filtros
            {hasActiveFilters && (
              <span className="ml-2 px-2 py-0.5 bg-primary text-white text-xs rounded-full">
                {Object.keys(filters).filter(k => filters[k as keyof TriageQueueFilters] !== undefined).length - 2}
              </span>
            )}
          </Button>

          {hasActiveFilters && (
            <Button
              variant="ghost"
              size="sm"
              onClick={handleClearFilters}
              className="cursor-pointer text-red-600 hover:text-red-700"
            >
              <X className="w-4 h-4 mr-1" />
              Limpar filtros
            </Button>
          )}
        </div>

        {/* Painel de filtros expansível */}
        {showFilters && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 pt-4 border-t">
            {/* Categoria */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">
                Categoria
              </label>
              <Select
                value={filters.category || "all"}
                onValueChange={(value) =>
                  onFiltersChange({ ...filters, category: value === "all" ? undefined : value, page: 1 })
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Todas" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas</SelectItem>
                  {categories?.map((cat: any) => (
                    <SelectItem key={cat.id} value={cat.id}>
                      {cat.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Bairro - Mock: lista estática */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">
                Bairro
              </label>
              <Select
                value={filters.neighborhood || "all"}
                onValueChange={(value) =>
                  onFiltersChange({ ...filters, neighborhood: value === "all" ? undefined : value, page: 1 })
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Todos" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  <SelectItem value="Centro">Centro</SelectItem>
                  <SelectItem value="Jardim das Flores">Jardim das Flores</SelectItem>
                  <SelectItem value="Vila Nova">Vila Nova</SelectItem>
                  <SelectItem value="Parque Industrial">Parque Industrial</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Anexos */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">
                Possui Anexos
              </label>
              <Select
                value={
                  filters.hasAttachments === undefined
                    ? "all"
                    : filters.hasAttachments
                    ? "yes"
                    : "no"
                }
                onValueChange={(value) => {
                  const hasAttachments =
                    value === "all" ? undefined : value === "yes";
                  onFiltersChange({ ...filters, hasAttachments, page: 1 });
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Todos" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  <SelectItem value="yes">Sim</SelectItem>
                  <SelectItem value="no">Não</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Data Inicial */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">
                Data Inicial
              </label>
              <Input
                type="date"
                value={filters.startDate || ""}
                onChange={(e) =>
                  onFiltersChange({ ...filters, startDate: e.target.value || undefined, page: 1 })
                }
              />
            </div>

            {/* Data Final */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">
                Data Final
              </label>
              <Input
                type="date"
                value={filters.endDate || ""}
                onChange={(e) =>
                  onFiltersChange({ ...filters, endDate: e.target.value || undefined, page: 1 })
                }
              />
            </div>
          </div>
        )}
      </div>
    </Card>
  );
};
